/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @description
 *
 * @created at 2024-11-14 13:21:46
 **/

import { atom, useAtom } from "jotai";
import { RefObject } from "react";
import { useAtomCallback } from "jotai/utils";

import { _T, _P } from "@rfice/language";
import { EventHandler } from "@rfice/eventhandler";
import { useToast } from "@/frameworks/designStuff/components";
import { JotaiStore } from "@/frameworks/libraries/commons";

import { EventID, SidebarID } from "@/frameworks/core/commons";
import { PeopleService, useGroup } from "@/frameworks/core/API";
import { useSidebarActions } from "../useSidebar";
import { useQueryClient } from "@tanstack/react-query";
import { GroupActions } from "@/frameworks/core/persistence";

type InitCreateWorkGroup = {
	CreateWorkGroupName: string;
	CreateWorkGroupDescription: string;
	CreateWorkGroupKeywords: string[];
	CreateWorkGroupAccessAllowType: WebServices.Group.Create.Request["accessAllowType"];
	SelectedColorName: keyof Colors;
	RepresentativeImage: Blob | MediaSource | null;
	AllowSearch: boolean;
	SelectedType: "color" | "image";
	ImageUrl: string | undefined;
	CheckedID: Group.CheckedType["ID"];
};

const initData = {
	CreateWorkGroupName: "",
	CreateWorkGroupDescription: "",
	CreateWorkGroupKeywords: [],
	CreateWorkGroupAccessAllowType: "PUBLIC",
	SelectedColorName: "deco6_50_100",
	RepresentativeImage: null,
	AllowSearch: false,
	SelectedType: "color",
	ImageUrl: undefined,
	CheckedID: "selectColor"
} as InitCreateWorkGroup;

const initCreateWorkGroup = atom<InitCreateWorkGroup>(initData);

export default function useCreateWorkGroup() {
	const [createWorkGroup, setCreateWorkGroup] = useAtom(initCreateWorkGroup);
	const queryClient = useQueryClient();

	const sidebarActions = useSidebarActions();
	const groupAPI = useGroup();
	const toast = useToast();

	const getState = useAtomCallback(
		(get) => {
			return get(initCreateWorkGroup);
		},
		{ store: JotaiStore }
	);

	const handleCreateWorkGroup = async () => {
		const params =
			createWorkGroup.SelectedType === "color"
				? {
						colorLogo: createWorkGroup.SelectedColorName.split("_")[0]
					}
				: {
						logoFile: createWorkGroup.RepresentativeImage
					};

		await groupAPI.create.execute({
			name: createWorkGroup.CreateWorkGroupName,
			keywords: createWorkGroup.CreateWorkGroupKeywords.join(","),
			description: createWorkGroup.CreateWorkGroupDescription,
			accessAllowType: createWorkGroup.CreateWorkGroupAccessAllowType,
			allowSearch: createWorkGroup.AllowSearch,
			...params
		});

		toast.show({
			type: "success",
			message: _P("{{name}} 그룹이 생성 되었습니다.", { name: createWorkGroup.CreateWorkGroupName }) as string
		});

		EventHandler.emit(EventID.onGroupCommand, "create");
		handleResetAll();
		sidebarActions.hide(SidebarID.CreateWorkGroup);
		await resetCachePeopleAndDMListAndSetNewGroup();
	};

	/**
	 * Update 04/05/2025: creating a group successfully when there is no group > Even after creating a group, it still says that there is no group
	 * Invalidate query cache of getPeopleList, getDMList & update group list in persis store after creating a group to solve problems
	 * @ticket: 232805
	 */
	const resetCachePeopleAndDMListAndSetNewGroup = async () => {
		await queryClient.invalidateQueries({ queryKey: ["getPeopleList"] });
		await queryClient.invalidateQueries({ queryKey: ["getDMList"] });
		const peopleAPI = new PeopleService({});
		const { data } = await peopleAPI.people.list.execute().catch(() => {
			return { data: null };
		});
		if (data) {
			GroupActions.instance.set_groups(data);
		}
	};

	const AllowTypeChecker = (type: WebServices.Group.Create.Request["accessAllowType"], allowSearch: boolean) => {
		switch (type) {
			case "PUBLIC":
				return false;
			case "PRIVATE":
				return !allowSearch;
			case "ENTIRE_PRIVATE":
				return true;
		}
	};

	const handleNameChange = (value: string) => {
		setCreateWorkGroup((prev) => ({
			...prev,
			CreateWorkGroupName: value
		}));
	};

	const handleAccessAllowType = (type: WebServices.Group.Create.Request["accessAllowType"]) => {
		setCreateWorkGroup((prev) => ({
			...prev,
			CreateWorkGroupAccessAllowType: type
		}));
	};

	const handleAddKeyword = (keyword: string) => {
		if (createWorkGroup.CreateWorkGroupKeywords.length > 2) {
			toast.show({ type: "error", message: _T("등록 최대 갯수를 넘어갔습니다.") });
			return;
		}
		setCreateWorkGroup((prev) => ({
			...prev,
			CreateWorkGroupKeywords: createWorkGroup.CreateWorkGroupKeywords.concat(keyword)
		}));
	};

	const handleRemoveKeyword = (keyword: string) => {
		setCreateWorkGroup((prev) => ({
			...prev,
			CreateWorkGroupKeywords: createWorkGroup.CreateWorkGroupKeywords.filter((item) => item !== keyword)
		}));
	};

	const handleRemoveAllKeyword = () => {
		setCreateWorkGroup((prev) => ({
			...prev,
			CreateWorkGroupKeywords: []
		}));
	};

	const handleAllowSearch = (active: boolean) => {
		setCreateWorkGroup((prev) => ({
			...prev,
			AllowSearch: !active
		}));
	};

	const handleSelectedColorName = (name: keyof Colors) => {
		setCreateWorkGroup((prev) => ({
			...prev,
			SelectedColorName: name,
			SelectedType: "color"
		}));
	};

	const handleRepresentativeImg = (data: Blob) => {
		setCreateWorkGroup((prev) => ({
			...prev,
			RepresentativeImage: data,
			SelectedType: "image"
		}));
	};

	const handleSetImageUrl = (data: string | undefined) => {
		setCreateWorkGroup((prev) => ({
			...prev,
			ImageUrl: data
		}));
	};

	const handleCheckedType = (type: Group.CheckedType["ID"]) => {
		setCreateWorkGroup((prev) => ({
			...prev,
			CheckedID: type
		}));
	};

	const handleReset = (ref: RefObject<HTMLInputElement>, callback: () => void) => {
		if (!ref.current) return;

		callback && callback();
		ref.current.value = "";
	};

	const handleResetAll = useAtomCallback(
		(get, set) => {
			set(initCreateWorkGroup, initData);
		},
		{ store: JotaiStore }
	);

	const handleDescription = (data: string) => {
		setCreateWorkGroup((prev) => ({ ...prev, CreateWorkGroupDescription: data }));
	};

	return {
		src: createWorkGroup.ImageUrl,
		name: createWorkGroup.CreateWorkGroupName,
		keywords: createWorkGroup.CreateWorkGroupKeywords,
		checkedID: createWorkGroup.CheckedID,
		description: createWorkGroup.CreateWorkGroupDescription,
		accessAllowType: createWorkGroup.CreateWorkGroupAccessAllowType,
		selectedColorName: createWorkGroup.SelectedColorName,
		representativeImg: createWorkGroup.RepresentativeImage,
		allowSearch: createWorkGroup.AllowSearch,
		isWrite: Boolean(
			createWorkGroup.CreateWorkGroupName ||
				createWorkGroup.CreateWorkGroupKeywords.length > 0 ||
				createWorkGroup.CreateWorkGroupDescription
		),
		handlers: {
			getState,
			handleReset,
			handleResetAll,
			handleNameChange,
			handleAddKeyword,
			handleRemoveKeyword,
			handleRemoveAllKeyword,
			handleDescription,
			handleCreateWorkGroup,
			handleAccessAllowType,
			handleSelectedColorName,
			handleRepresentativeImg,
			handleAllowSearch,
			handleAllowTypeChecker: AllowTypeChecker,
			handleSetImageUrl,
			handleCheckedType
		}
	};
}
